import React, { useState, useEffect, useContext, useRef } from 'react';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBell, 
    faPlus, 
    faExternalLinkAlt, 
    faBullhorn,
    faArrowLeft,
    faEdit,
    faTrash,
    faDownload,
    faEye
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './header';
import Footer from './footer';
import { useCustomAlert } from './CustomAlert';

const GroupAnnouncements = () => {
    const { user } = useContext(MyContext);
    const { groupId } = useParams();
    const navigate = useNavigate();
    const { showAlert, showConfirm, AlertComponent } = useCustomAlert();
    
    const [group, setGroup] = useState(null);
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRole, setUserRole] = useState('member');
    const [isGroupCreator, setIsGroupCreator] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        if (user && user.user_id && groupId) {
            loadGroupData();
            loadAnnouncements();
        }
    }, [user, groupId, currentPage]);

    // Initialize Bootstrap dropdowns
    useEffect(() => {
        const initializeDropdowns = () => {
            // Bootstrap should auto-initialize dropdowns, but let's ensure they work
            if (typeof window !== 'undefined' && window.bootstrap) {
                const dropdownElements = document.querySelectorAll('[data-bs-toggle="dropdown"]:not(.dropdown-initialized)');
                dropdownElements.forEach(element => {
                    new window.bootstrap.Dropdown(element);
                    element.classList.add('dropdown-initialized');
                });
            }
        };

        // Initialize dropdowns after announcements are loaded
        if (announcements.length > 0) {
            setTimeout(initializeDropdowns, 100);
        }
    }, [announcements]);

    // Track views for announcements when they are first loaded
    const trackedViewsRef = useRef(new Set());
    
    useEffect(() => {
        if (announcements.length > 0 && user && user.user_id) {
            announcements.forEach(announcement => {
                if (!trackedViewsRef.current.has(announcement.id)) {
                    trackAnnouncementView(announcement.id);
                    trackedViewsRef.current.add(announcement.id);
                }
            });
        }
    }, [announcements, user]);

    const loadGroupData = async () => {
        try {
            const response = await fetch(`${backendUrl}/get_user_groups.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id,
                    status: 'active'
                })
            });

            const data = await response.json();
            if (data.success) {
                const currentGroup = data.data.find(g => g.group_id == groupId);
                if (currentGroup) {
                    setGroup(currentGroup);
                    setUserRole(currentGroup.role);
                    setIsGroupCreator(currentGroup.created_by == user.user_id);
                } else {
                    setError('Group not found or access denied');
                }
            }
        } catch (err) {
            setError('Failed to load group data');
            console.error('Error loading group data:', err);
        }
    };

    const loadAnnouncements = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_group_announcements.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    group_id: groupId,
                    user_id: user.user_id,
                    page: currentPage,
                    limit: 10
                })
            });

            const data = await response.json();
            console.log('Announcements API response:', data);
            if (data.success) {
                console.log('Announcements data:', data.data.announcements);
                setAnnouncements(data.data.announcements || []);
                setTotalPages(data.data.pagination.total_pages || 1);
            } else {
                setError(data.messages || 'Failed to load announcements');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error loading announcements:', err);
        } finally {
            setLoading(false);
        }
    };

    const trackAnnouncementView = async (announcementId) => {
        try {
            const response = await fetch(`${backendUrl}/track_announcement_view.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    announcement_id: announcementId,
                    user_id: user.user_id
                })
            });

            const data = await response.json();
            if (data.success && !data.data.already_viewed) {
                // Update the view count in the local state
                setAnnouncements(prevAnnouncements => 
                    prevAnnouncements.map(announcement => 
                        announcement.id === announcementId 
                            ? { ...announcement, view_count: announcement.view_count + 1 }
                            : announcement
                    )
                );
            }
        } catch (err) {
            console.error('Error tracking announcement view:', err);
        }
    };

    const handleEditAnnouncement = (announcement) => {
        setEditingAnnouncement(announcement);
        setShowEditModal(true);
    };

    const handleDeleteAnnouncement = async (announcementId) => {
        showConfirm(
            'Delete Announcement',
            'Are you sure you want to delete this announcement? This action cannot be undone.',
            async () => {
                try {
            const response = await fetch(`${backendUrl}/delete_group_announcement.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    announcement_id: announcementId,
                    user_id: user.user_id
                })
            });

            const data = await response.json();
            if (data.success) {
                // Remove the announcement from the local state
                setAnnouncements(prevAnnouncements => 
                    prevAnnouncements.filter(announcement => announcement.id !== announcementId)
                );
                showAlert('success', 'Success', 'Announcement deleted successfully');
                } else {
                    showAlert('error', 'Error', data.messages || 'Failed to delete announcement');
                }
            } catch (err) {
                showAlert('error', 'Error', 'Network error. Please try again later.');
                console.error('Error deleting announcement:', err);
            }
        },
        {
            type: 'danger',
            confirmText: 'Delete',
            cancelText: 'Cancel'
        }
    );
};

    // Helper function to safely parse dates for Safari compatibility
    const parseDate = (dateString) => {
        if (!dateString) return null;
        
        // If it's already a Date object, return it
        if (dateString instanceof Date) return dateString;
        
        // Manual parsing for "YYYY-MM-DD HH:MM:SS" format
        const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
        if (match) {
            const [, year, month, day, hour, minute, second] = match;
            // Create date as local time (not UTC)
            const date = new Date(
                parseInt(year, 10),
                parseInt(month, 10) - 1, // Month is 0-indexed
                parseInt(day, 10),
                parseInt(hour, 10),
                parseInt(minute, 10),
                parseInt(second, 10)
            );
            
            return isNaN(date.getTime()) ? null : date;
        }
        
        // Fallback to regular Date parsing
        try {
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? null : date;
        } catch (error) {
            console.error('Error parsing date:', dateString, error);
            return null;
        }
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        
        const date = parseDate(timestamp);
        if (!date) return 'Invalid date';
        
        const now = new Date();
        const diff = now - date;
        
        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
        
        return date.toLocaleDateString();
    };

    const canCreateAnnouncements = userRole === 'admin' || isGroupCreator;

    if (loading && !group) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="ms-3">Loading announcements...</div>
            </div>
        );
    }

    if (error && !group) {
        return (
            <div className="container-fluid p-3">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100 d-flex flex-column">
            <Header />
            
            <div className="container-fluid p-3 flex-grow-1" style={{ marginTop: '80px', marginBottom: '60px' }}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center">
                        <div>
                            <h2 className="mb-0">
                                <FontAwesomeIcon icon={faBell} className="me-2" />
                                Announcements
                            </h2>
                            <small className="text-muted">{group?.group_name}</small>
                        </div>
                    </div>
                    {canCreateAnnouncements && (
                        <button 
                            className="btn btn-primary"
                            onClick={() => setShowCreateModal(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-1" />
                            Create Announcement
                        </button>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                {/* Announcements List */}
                {loading ? (
                    <div className="d-flex justify-content-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : announcements.length === 0 ? (
                    <div className="text-center py-5">
                        <FontAwesomeIcon icon={faBell} size="3x" className="text-muted mb-3" />
                        <h5 className="text-muted">No announcements yet</h5>
                        <p className="text-muted">
                            {canCreateAnnouncements 
                                ? 'Create the first announcement to keep members informed'
                                : 'Check back later for group announcements'
                            }
                        </p>
                        {canCreateAnnouncements && (
                            <button 
                                className="btn btn-primary"
                                onClick={() => setShowCreateModal(true)}
                            >
                                <FontAwesomeIcon icon={faPlus} className="me-1" />
                                Create First Announcement
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="row">
                        {announcements.map((announcement) => {
                            console.log('Rendering announcement:', announcement);
                            
                            return (
                            <div key={announcement.id} className="col-12 mb-3">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex align-items-start">
                                            {announcement.is_important && (
                                                <div className="me-3">
                                                    <FontAwesomeIcon 
                                                        icon={faBullhorn} 
                                                        className="text-warning" 
                                                        size="lg"
                                                    />
                                                </div>
                                            )}
                                            <div className="flex-grow-1">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <h5 className="card-title mb-0">
                                                        {announcement.title}
                                                        {announcement.is_important && (
                                                            <span className="badge bg-warning ms-2">Important</span>
                                                        )}
                                                    </h5>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <small className="text-muted">
                                                            {formatTime(announcement.created_at)}
                                                        </small>
                                                        <small className="text-muted d-flex align-items-center">
                                                            <FontAwesomeIcon icon={faEye} className="me-1" />
                                                            {announcement.view_count || 0}
                                                        </small>
                                                        {canCreateAnnouncements && (
                                                            <div className="dropdown">
                                                                <button 
                                                                    className="btn btn-sm btn-outline-secondary dropdown-toggle"
                                                                    type="button"
                                                                    id={`dropdownMenuButton-${announcement.id}`}
                                                                    data-bs-toggle="dropdown"
                                                                    aria-expanded="false"
                                                                >
                                                                    ⋯
                                                                </button>
                                                                <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton-${announcement.id}`}>
                                                                    <li>
                                                                        <button 
                                                                            className="dropdown-item"
                                                                            onClick={() => handleEditAnnouncement(announcement)}
                                                                        >
                                                                            <FontAwesomeIcon icon={faEdit} className="me-2" />
                                                                            Edit
                                                                        </button>
                                                                    </li>
                                                                    <li>
                                                                        <button 
                                                                            className="dropdown-item text-danger"
                                                                            onClick={() => handleDeleteAnnouncement(announcement.id)}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTrash} className="me-2" />
                                                                            Delete
                                                                        </button>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="d-flex align-items-center mb-3">
                                                    <img 
                                                        src={announcement.sender.photo || '/avatar.jpg'} 
                                                        alt={announcement.sender.name}
                                                        className="rounded-circle me-2"
                                                        style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                                    />
                                                    <small className="text-muted">
                                                        By <strong>{announcement.sender.name}</strong>
                                                    </small>
                                                </div>
                                                
                                                <p className="card-text mb-3">{announcement.message}</p>
                                                
                                                {announcement.image_url && (
                                                    <div className="mb-3">
                                                        <div className="position-relative">
                                                            <img 
                                                                src={announcement.image_url} 
                                                                alt="Announcement flyer" 
                                                                className="img-fluid rounded"
                                                                style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }}
                                                            />
                                                            <div className="position-absolute top-0 end-0 m-2">
                                                                <a 
                                                                    href={announcement.image_url} 
                                                                    download={`announcement-${announcement.id}-flyer.jpg`}
                                                                    className="btn btn-sm btn-dark"
                                                                    title="Download image"
                                                                >
                                                                    <FontAwesomeIcon icon={faDownload} />
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {announcement.has_url && (
                                                    <div className="mt-3">
                                                        <a 
                                                            href={announcement.url} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="btn btn-outline-primary"
                                                        >
                                                            <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                                                            {announcement.url_text || 'View Link'}
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <nav>
                            <ul className="pagination">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link"
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </button>
                                </li>
                                {[...Array(totalPages)].map((_, index) => (
                                    <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                                        <button 
                                            className="page-link"
                                            onClick={() => setCurrentPage(index + 1)}
                                        >
                                            {index + 1}
                                        </button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button 
                                        className="page-link"
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}
            </div>

            <Footer />

            {/* Create Announcement Modal */}
            {showCreateModal && (
                <CreateAnnouncementModal 
                    groupId={groupId}
                    onClose={() => setShowCreateModal(false)}
                    onAnnouncementCreated={loadAnnouncements}
                />
            )}

            {/* Edit Announcement Modal */}
            {showEditModal && editingAnnouncement && (
                <EditAnnouncementModal 
                    announcement={editingAnnouncement}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingAnnouncement(null);
                    }}
                    onAnnouncementUpdated={loadAnnouncements}
                />
            )}

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

// Create Announcement Modal Component
const CreateAnnouncementModal = ({ groupId, onClose, onAnnouncementCreated }) => {
    const { user } = useContext(MyContext);
    const { showAlert, showConfirm, AlertComponent } = useCustomAlert();
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        url: '',
        url_text: '',
        is_important: false
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            setError('Title is required');
            return;
        }
        
        if (!formData.message.trim()) {
            setError('Message is required');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            let imageUrl = null;
            
            // Upload image file to Cloudinary if provided
            if (imageFile) {
                const uploadFormData = new FormData();
                uploadFormData.append('image', imageFile);
                uploadFormData.append('user_id', user.user_id);
                uploadFormData.append('group_id', groupId);
                
                const uploadResponse = await fetch(`${backendUrl}/upload_to_cloudinary.php`, {
                    method: 'POST',
                    body: uploadFormData
                });
                
                const uploadData = await uploadResponse.json();
                
                if (uploadData.success && uploadData.data && uploadData.data.secure_url) {
                    imageUrl = uploadData.data.secure_url;
                    console.log('Image uploaded successfully:', imageUrl);
                } else {
                    console.error('Image upload failed:', uploadData);
                    setError(uploadData.messages || 'Failed to upload image');
                    return;
                }
            }

            const announcementData = {
                group_id: groupId,
                sender_id: user.user_id,
                title: formData.title.trim(),
                message: formData.message.trim(),
                url: formData.url.trim() || null,
                url_text: formData.url_text.trim() || null,
                is_important: formData.is_important ? 1 : 0,
                image_url: imageUrl
            };
            
            console.log('Sending announcement data:', announcementData);
            
            const response = await fetch(`${backendUrl}/create_group_announcement.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(announcementData)
            });

            const data = await response.json();
            console.log('Announcement creation response:', data);

            if (data.success) {
                onAnnouncementCreated();
                onClose();
            } else {
                setError(data.messages || 'Failed to create announcement');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error creating announcement:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Create Announcement</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="title" className="form-label">Title *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    placeholder="Enter announcement title"
                                    maxLength={255}
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="message" className="form-label">Message *</label>
                                <textarea
                                    className="form-control"
                                    id="message"
                                    rows="4"
                                    value={formData.message}
                                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                                    placeholder="Enter announcement message"
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="image" className="form-label">Flyer/Image (Optional)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="image"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setImageFile(file);
                                            const reader = new FileReader();
                                            reader.onload = (e) => setImagePreview(e.target.result);
                                            reader.readAsDataURL(file);
                                        } else {
                                            setImageFile(null);
                                            setImagePreview(null);
                                        }
                                    }}
                                />
                                {imagePreview && (
                                    <div className="mt-2">
                                        <img 
                                            src={imagePreview} 
                                            alt="Preview" 
                                            className="img-thumbnail" 
                                            style={{ maxWidth: '200px', maxHeight: '200px' }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="mb-3">
                                <label htmlFor="url" className="form-label">Link URL (Optional)</label>
                                <input
                                    type="url"
                                    className="form-control"
                                    id="url"
                                    value={formData.url}
                                    onChange={(e) => setFormData({...formData, url: e.target.value})}
                                    placeholder="https://example.com"
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="url_text" className="form-label">Link Text (Optional)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="url_text"
                                    value={formData.url_text}
                                    onChange={(e) => setFormData({...formData, url_text: e.target.value})}
                                    placeholder="Click here to view"
                                />
                            </div>

                            <div className="mb-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="is_important"
                                        checked={formData.is_important}
                                        onChange={(e) => setFormData({...formData, is_important: e.target.checked})}
                                    />
                                    <label className="form-check-label" htmlFor="is_important">
                                        Mark as important
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Creating...
                                    </>
                                ) : (
                                    'Create Announcement'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

// Edit Announcement Modal Component
const EditAnnouncementModal = ({ announcement, onClose, onAnnouncementUpdated }) => {
    const { user } = useContext(MyContext);
    const { showAlert, showConfirm, AlertComponent } = useCustomAlert();
    const [formData, setFormData] = useState({
        title: announcement.title,
        message: announcement.message,
        url: announcement.url || '',
        url_text: announcement.url_text || '',
        is_important: announcement.is_important
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(announcement.image_url);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            setError('Title is required');
            return;
        }
        
        if (!formData.message.trim()) {
            setError('Message is required');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            let imageUrl = announcement.image_url; // Keep existing image by default
            
            // Upload new image file to Cloudinary if provided
            if (imageFile) {
                const uploadFormData = new FormData();
                uploadFormData.append('image', imageFile);
                uploadFormData.append('user_id', user.user_id);
                uploadFormData.append('group_id', announcement.group_id);
                
                const uploadResponse = await fetch(`${backendUrl}/upload_to_cloudinary.php`, {
                    method: 'POST',
                    body: uploadFormData
                });
                
                const uploadData = await uploadResponse.json();
                
                if (uploadData.success && uploadData.data && uploadData.data.secure_url) {
                    imageUrl = uploadData.data.secure_url;
                    console.log('Image uploaded successfully:', imageUrl);
                } else {
                    console.error('Image upload failed:', uploadData);
                    setError(uploadData.messages || 'Failed to upload image');
                    return;
                }
            }

            const announcementData = {
                announcement_id: announcement.id,
                user_id: user.user_id,
                title: formData.title.trim(),
                message: formData.message.trim(),
                url: formData.url.trim() || null,
                url_text: formData.url_text.trim() || null,
                is_important: formData.is_important ? 1 : 0,
                image_url: imageUrl
            };
            
            console.log('Sending announcement update data:', announcementData);
            
            const response = await fetch(`${backendUrl}/update_group_announcement.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(announcementData)
            });

            const data = await response.json();
            console.log('Announcement update response:', data);

            if (data.success) {
                onAnnouncementUpdated();
                onClose();
                showAlert('success', 'Success', 'Announcement updated successfully');
            } else {
                setError(data.messages || 'Failed to update announcement');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error updating announcement:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Announcement</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="title" className="form-label">Title *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    placeholder="Enter announcement title"
                                    maxLength={255}
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="message" className="form-label">Message *</label>
                                <textarea
                                    className="form-control"
                                    id="message"
                                    rows="4"
                                    value={formData.message}
                                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                                    placeholder="Enter announcement message"
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="image" className="form-label">Flyer/Image (Optional)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="image"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setImageFile(file);
                                            const reader = new FileReader();
                                            reader.onload = (e) => setImagePreview(e.target.result);
                                            reader.readAsDataURL(file);
                                        } else {
                                            setImageFile(null);
                                            setImagePreview(announcement.image_url);
                                        }
                                    }}
                                />
                                {imagePreview && (
                                    <div className="mt-2">
                                        <img 
                                            src={imagePreview} 
                                            alt="Preview" 
                                            className="img-thumbnail" 
                                            style={{ maxWidth: '200px', maxHeight: '200px' }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="mb-3">
                                <label htmlFor="url" className="form-label">Link URL (Optional)</label>
                                <input
                                    type="url"
                                    className="form-control"
                                    id="url"
                                    value={formData.url}
                                    onChange={(e) => setFormData({...formData, url: e.target.value})}
                                    placeholder="https://example.com"
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="url_text" className="form-label">Link Text (Optional)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="url_text"
                                    value={formData.url_text}
                                    onChange={(e) => setFormData({...formData, url_text: e.target.value})}
                                    placeholder="Click here to view"
                                />
                            </div>

                            <div className="mb-3">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="is_important"
                                        checked={formData.is_important}
                                        onChange={(e) => setFormData({...formData, is_important: e.target.checked})}
                                    />
                                    <label className="form-check-label" htmlFor="is_important">
                                        Mark as Important
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary" 
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Updating...
                                    </>
                                ) : (
                                    'Update Announcement'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Custom Alert */}
            <AlertComponent />
        </div>
    );
};

export default GroupAnnouncements;
